import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";
import { UserPlus, ShieldAlert, CheckCircle2 } from "lucide-react";

export default function Governance() {
  const { lang } = useI18n();
  const [rolesCat, setRolesCat] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("end_user");

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get("/roles/catalogue"),
          api.get("/roles/matrix"),
        ]);
        setRolesCat(c.data.roles || []);
        setMatrix(m.data.permissions || {});
      } catch {}
    })();
  }, []);

  const assign = async () => {
    if (!email.trim()) return;
    try {
      await api.post("/roles/assign", { email: email.trim(), role });
      altioraToast.success(lang === "fr" ? "Rôle assigné" : "Role assigned");
      setEmail("");
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    }
  };

  return (
    <div className="min-h-full" data-testid="governance-root">
      <TopBar
        title={lang === "fr" ? "Gouvernance & Rôles" : "Governance & Roles"}
        subtitle={lang === "fr" ? "Six niveaux hiérarchisés. Attribution et matrice de permissions." : "Six-tier hierarchy. Assignment and permission matrix."}
      />
      <div className="px-6 md:px-10 py-8 md:py-10 max-w-6xl mx-auto space-y-8">
        {/* Roles catalogue */}
        <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-4">
            {lang === "fr" ? "Six rôles" : "Six roles"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {rolesCat.map((r) => (
              <div key={r.key} data-testid={`governance-role-${r.key}`} className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">{r.key}</div>
                <div className="mt-2 font-display text-base text-[#1F2937] tracking-tight">{r[`label_${lang}`]}</div>
                <div className="mt-1 text-xs text-[#5B5B62] leading-relaxed">{r[`description_${lang}`]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Assign */}
        <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-4 flex items-center gap-2">
            <UserPlus className="h-3 w-3" strokeWidth={2} />
            {lang === "fr" ? "Assigner un rôle" : "Assign a role"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              data-testid="governance-email"
              type="email"
              placeholder={lang === "fr" ? "Email de l'utilisateur" : "User email"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] focus:border-[#0B2545] focus:outline-none"
            />
            <select
              data-testid="governance-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
            >
              {rolesCat.map((r) => <option key={r.key} value={r.key}>{r[`label_${lang}`]}</option>)}
            </select>
            <button
              data-testid="governance-assign"
              onClick={assign}
              className="inline-flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-4 py-2 rounded-md altiora-transition"
            >
              <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />
              {lang === "fr" ? "Assigner" : "Assign"}
            </button>
          </div>
        </div>

        {/* Matrix */}
        <div className="rounded-lg border border-[#E4E1D8] bg-white">
          <div className="px-6 py-4 border-b border-[#E4E1D8] font-display text-lg text-[#1F2937] tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
            {lang === "fr" ? "Matrice des permissions" : "Permission matrix"}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E4E1D8]">
                  <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                    {lang === "fr" ? "Capacité" : "Capability"}
                  </th>
                  {rolesCat.map((r) => (
                    <th key={r.key} className="px-3 py-3 font-mono text-[9px] uppercase tracking-widest text-[#5B5B62]" title={r.key}>
                      {r.key.replace("_", " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(matrix).map(([cap, allowed]) => (
                  <tr key={cap} className="border-b border-[#E4E1D8]/60">
                    <td className="px-4 py-2.5 font-mono text-[11px] text-[#1F2937]">{cap}</td>
                    {rolesCat.map((r) => (
                      <td key={r.key} className="px-3 py-2.5 text-center">
                        {(allowed || []).includes(r.key)
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-[#059669] mx-auto" strokeWidth={2} />
                          : <span className="text-[#E4E1D8]">·</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
